import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Grid, Stack, Text, Title } from '@mantine/core';
import {
  useRegistrationIntakeStatusService,
  useCareRequestStatusService,
  useRegistrationStatusService,
  useACCourseService,
  useFacilitatorService,
  useLocationService,
  useCareCaseStatusService,
  useCareNeedService,
  useLeaderService,
} from '@actschurch/ccms-sdk';
import type { SourceKey, AnySourceConfig } from './types';
import { SOURCES, ENRICHED_KEYS } from './Sources';
import { useColumnSelection } from './Hooks/useColumnSelection';
import { useExportEnrichment } from './Hooks/useExportEnrichment';
import { useReportFilters } from './Hooks/useReportFilters';
import { useReportTable } from './Hooks/useReportTable';
import { useExportAll } from './Hooks/useExportAll';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { StreamProgress } from '@/components';
import { SourceSelector } from './Components/SourceSelector';
import { FiltersBar } from './Components/FiltersBar';
import { FilterDrawer } from './Components/FilterDrawer';
import { ColumnPicker } from './Components/ColumnPicker';
import { PreviewTable } from './Components/PreviewTable';
import { ExportBar } from './Components/ExportBar';
import { LockedSection } from './Components/LockedSection';
import { EnrichmentPanel } from './Components/EnrichmentPanel';
import { MissingDetailsModal } from './Components/MissingDetailsModal';
import { MemberLookupWarningModal } from './Components/MemberLookupWarningModal';
import { IncompletePeopleModal } from './Components/IncompletePeopleModal';

type Opt = { value: string; label: string };

export default function AdministrationExportPage() {
  const [sourceKey, setSourceKey] = useState<SourceKey>('intake');
  const source: AnySourceConfig = SOURCES[sourceKey];
  const isPaged = source.mode === 'paged';

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const cols = useColumnSelection(source);
  const filters = useReportFilters(source);

  // ── Request and Intake exports: missing-data run (Source 2 detail, optional Source 3 members) ──
  const enrichEntity = sourceKey === 'registration' ? 'registration'
    : sourceKey === 'case' ? 'case'
      : sourceKey === 'intake' ? 'intake' : 'request';
  const enrich = useExportEnrichment(enrichEntity);
  const enrichKeys = ENRICHED_KEYS[sourceKey];
  /** True when a picked column (or its Person B twin) needs the run before it has real values. */
  const needsEnrichment = Boolean(enrichKeys) && cols.selectedKeys.some((k) => enrichKeys!.has(k));
  const enrichedRows = needsEnrichment ? enrich.rows : null;
  const [warnOpen, setWarnOpen] = useState(false);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  // Close the list when the results are discarded (filters or search change, Start over, Cancel).
  useEffect(() => { if (enrich.stage !== 'done') setIncompleteOpen(false); }, [enrich.stage]);

  const table = useReportTable({
    sourceKey, source,
    applied: filters.applied,
    // Person B twins ride along here; the picker below still gets `cols.selectedColumns`.
    selectedColumns: cols.exportColumns,
    search: debouncedSearch,
    overrideRows: enrichedRows,
  });

  const exportAll = useExportAll({
    fetchPage: table.fetchPage,
    totalCount: table.serverTotalCount,
  });

  // ── SDK services for status + filter option lookups ─────────────────────
  const intakeStatusSvc   = useRegistrationIntakeStatusService();
  const requestStatusSvc  = useCareRequestStatusService();
  const regStatusSvc      = useRegistrationStatusService();
  const caseStatusSvc     = useCareCaseStatusService();
  const courseSvc         = useACCourseService();
  const facilitatorSvc    = useFacilitatorService();
  const locationSvc       = useLocationService();
  const needSvc           = useCareNeedService();
  const leaderSvc         = useLeaderService();

  const [options, setOptions] = useState<Record<string, Opt[]>>({});
  const [courseDates, setCourseDates] = useState<Opt[]>([]);

  useEffect(() => {
    let cancelled = false;
    setOptions({});
    setCourseDates([]);

    const assign = (key: string, list: Opt[]) =>
      !cancelled && setOptions((prev) => ({ ...prev, [key]: list }));

    if (sourceKey === 'intake') {
      intakeStatusSvc.getAll()
        .then((l) => assign('status', (Array.isArray(l) ? l : []).map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` }))))
        .catch((e) => console.error('[export] intake statuses:', e));
      courseSvc.getAllViews()
        .then((l) => assign('course', (Array.isArray(l) ? l : []).map((c) => ({
          value: String(c.courseID), label: c.name ?? `Course ${c.courseID}`,
        }))))
        .catch((e) => console.error('[export] intake courses:', e));
      locationSvc.getAll()
        .then((l) => assign('location', (Array.isArray(l) ? l : []).filter((x) => x.isActive).map((x) => ({
          value: String(x.locationID), label: x.name,
        }))))
        .catch((e) => console.error('[export] intake locations:', e));
    } else if (sourceKey === 'request') {
      requestStatusSvc.getAll()
        .then((l) => assign('status', (Array.isArray(l) ? l : []).map((s) => ({ value: String(s.statusID), label: s.name ?? `Status ${s.statusID}` }))))
        .catch((e) => console.error('[export] request statuses:', e));
      needSvc.getAll()
        .then((l) => assign('need', (Array.isArray(l) ? l : []).map((x) => ({
          value: String(x.needID), label: x.name ?? `Need ${x.needID}`,
        }))))
        .catch((e) => console.error('[export] request needs:', e));
      locationSvc.getAll()
        .then((l) => assign('location', (Array.isArray(l) ? l : []).filter((x) => x.isActive).map((x) => ({
          value: String(x.locationID), label: x.name,
        }))))
        .catch((e) => console.error('[export] request locations:', e));
    } else if (sourceKey === 'registration') {
      Promise.allSettled([
        regStatusSvc.getAll(),
        courseSvc.getAll(),
        facilitatorSvc.list(),
        locationSvc.getAll(),
      ]).then(([s, c, f, l]) => {
        if (s.status === 'fulfilled') assign('status', (Array.isArray(s.value) ? s.value : []).map((x: any) => ({ value: String(x.statusID), label: x.name ?? `Status ${x.statusID}` })));
        if (c.status === 'fulfilled') assign('course', (Array.isArray(c.value) ? c.value : []).map((x: any) => ({ value: String(x.courseID), label: x.name ?? `Course ${x.courseID}` })));
        if (f.status === 'fulfilled') assign('facilitator', (Array.isArray(f.value) ? f.value : []).map((x: any) => ({ value: String(x.userId), label: [x.firstName, x.surname].filter(Boolean).join(' ') || x.fullName || String(x.userId) })));
        if (l.status === 'fulfilled') assign('location', (Array.isArray(l.value) ? l.value : []).filter((x: any) => x.isActive).map((x: any) => ({ value: String(x.locationID), label: x.name })));
      });
    } else if (sourceKey === 'case') {
      Promise.allSettled([
        caseStatusSvc.getAll(),
        needSvc.getAll(),
        locationSvc.getAll(),
        leaderSvc.list(),
      ]).then(([s, n, l, ld]) => {
        if (s.status === 'fulfilled') assign('status', (Array.isArray(s.value) ? s.value : []).map((x: any) => ({ value: String(x.statusID), label: x.name ?? `Status ${x.statusID}` })));
        if (n.status === 'fulfilled') assign('need', (Array.isArray(n.value) ? n.value : []).map((x: any) => ({ value: String(x.needID), label: x.name ?? `Need ${x.needID}` })));
        if (l.status === 'fulfilled') assign('location', (Array.isArray(l.value) ? l.value : []).filter((x: any) => x.isActive).map((x: any) => ({ value: String(x.locationID), label: x.name })));
        if (ld.status === 'fulfilled') assign('leader', (Array.isArray(ld.value) ? ld.value : []).map((x: any) => ({ value: String(x.userId), label: [x.firstName, x.surname].filter(Boolean).join(' ') || x.fullName || String(x.userId) })));
      });
    }

    return () => { cancelled = true; };
  }, [sourceKey, intakeStatusSvc, requestStatusSvc, regStatusSvc, caseStatusSvc, courseSvc, facilitatorSvc, locationSvc, needSvc, leaderSvc]);

  // Course-date cascade for registration: refetch when the draft's course changes.
  useEffect(() => {
    if (sourceKey !== 'registration') return;
    const courseID = filters.draft.course as string | undefined;
    if (!courseID) { setCourseDates([]); return; }
    courseSvc.getDates(Number(courseID))
      .then((d) => setCourseDates((Array.isArray(d) ? d : []).map((x: any) => ({
        value: String(x.dateID),
        label: x.periodDisplay ?? `Date ${x.dateID}`,
      }))))
      .catch((e) => console.error('[export] course dates:', e));
  }, [sourceKey, filters.draft.course, courseSvc]);

  const runtimeOptions = useMemo(
    () => ({ ...options, date: courseDates }),
    [options, courseDates],
  );

  // The run is tied to the filters and search it started with. Once it is done they unlock, and
  // changing either makes the finished rows stale, so they are discarded (back to Configure).
  useEffect(() => { enrich.reset(); }, [filters.applied, debouncedSearch, sourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSourceChange = (next: SourceKey) => {
    enrich.reset();
    setSourceKey(next);
    setSearchTerm('');
    cols.resetFor(SOURCES[next]);
    filters.clearFilters();
    table.resetSort();
    exportAll.reset();
  };

  const fetchAllForExport = useCallback(async () => {
    const rows = await exportAll.exportAll();
    return rows;
  }, [exportAll]);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <div>
          <Title order={2}>Export Data</Title>
          <Text c="dimmed" size="sm">
            Choose a data source, pick the columns you need, and export. Exported files may contain
            sensitive information handle them according to your organisation's data policies.
          </Text>
        </div>

        <LockedSection locked={enrich.locked}>
          <SourceSelector value={sourceKey} sources={SOURCES} onChange={handleSourceChange} />
        </LockedSection>

        <LockedSection locked={enrich.locked}>
          <FiltersBar
            appliedCount={filters.appliedCount}
            matchCount={table.totalItems}
            totalCount={table.serverTotalCount}
            entityLabel={source.plural.toLowerCase()}
            onOpen={filters.openFilters}
            onClear={filters.clearFilters}
            showSearch
            search={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </LockedSection>

        {/* Streamed sources (registration, case) load everything up front — show how far along it is. */}
        {!isPaged && (
          <StreamProgress
            loading={table.stream.loading}
            progress={table.stream.progress}
            loadedCount={table.stream.loadedCount}
            label={source.plural.toLowerCase()}
          />
        )}

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <LockedSection locked={enrich.locked}>
            <ColumnPicker
              groupedAvailable={cols.groupedAvailable}
              selectedKeys={cols.selectedKeys}
              selectedColumns={cols.selectedColumns}
              search={cols.search}
              onSearchChange={cols.setSearch}
              onToggle={cols.toggleColumn}
              onReorder={cols.reorderColumns}
              onSelectAll={cols.selectAll}
              onSelectNone={cols.selectNone}
              onSelectDefault={cols.selectDefault}
            />
            </LockedSection>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8 }}>
            <LockedSection locked={enrich.locked}>
            <PreviewTable
              sourceLabel={source.label}
              hasColumns={cols.selectedColumns.length > 0}
              hasMatches={table.totalItems > 0}
              rows={table.paginatedRows}
              columns={table.tableColumns}
              page={table.activePage}
              pageSize={table.pageSize}
              totalItems={table.totalItems}
              loading={table.loading}
              onSort={table.handleSort}
              onPageChange={table.setActivePage}
              onPageSizeChange={table.setPageSize}
            />
            </LockedSection>
          </Grid.Col>
        </Grid>

        {needsEnrichment && (
          <EnrichmentPanel
            entity={enrichEntity}
            stage={enrich.stage}
            progress={enrich.progress}
            summary={enrich.summary}
            // Streamed (cases) counts what is left after the client-side filters and search.
            canStart={!table.loading && !table.error
              && (isPaged ? table.serverTotalCount : table.totalItems) > 0}
            onStart={() => enrich.start(isPaged
              ? { fetchPage: table.fetchPage, totalCount: table.serverTotalCount }
              : { rows: table.sortedRows })}
            onCancel={enrich.cancel}
            onStartOver={enrich.reset}
            incompleteCount={enrich.incomplete.length}
            onViewIncomplete={() => setIncompleteOpen(true)}
          />
        )}

        <ExportBar
          rows={table.sortedRows}
          columns={table.tableColumns}
          columnCount={table.tableColumns.length}
          filename={`${sourceKey}-export`}
          mode={source.mode}
          totalCount={table.serverTotalCount}
          fetchAll={isPaged ? fetchAllForExport : undefined}
          busy={isPaged ? exportAll.busy : undefined}
          progress={isPaged ? exportAll.progress : undefined}
          localRows={enrichedRows}
          blockedReason={
            needsEnrichment && enrich.stage !== 'done'
              ? (enrich.locked
                ? 'Details are still being filled.'
                : 'Press Start to fill the missing details first.')
              : null
          }
        />
      </Stack>

      <FilterDrawer
        opened={filters.filtersOpen}
        source={source}
        sourceKey={sourceKey}
        draft={filters.draft}
        runtimeOptions={runtimeOptions}
        onChange={filters.setDraftValue}
        onApply={filters.applyFilters}
        onClear={filters.clearFilters}
        onClose={filters.closeFilters}
      />

      <MissingDetailsModal
        entity={enrichEntity}
        opened={enrich.stage === 'review' && !warnOpen}
        review={enrich.review}
        retrying={enrich.retrying}
        onRetry={enrich.retryFailed}
        onLookup={() => setWarnOpen(true)}
        onContinue={enrich.continueWithout}
        onCancel={enrich.cancel}
      />
      <MemberLookupWarningModal
        opened={warnOpen && enrich.stage === 'review'}
        plan={enrich.review?.plan ?? null}
        onBack={() => setWarnOpen(false)}
        onConfirm={() => { setWarnOpen(false); enrich.lookUpMembers(); }}
      />
      <IncompletePeopleModal
        opened={incompleteOpen}
        onClose={() => setIncompleteOpen(false)}
        rows={enrich.incomplete}
        entity={enrichEntity}
      />
    </Container>
  );
}